import { createClient } from '@supabase/supabase-js';
import { AUTH_CONSTANTS } from './auth/constants';

/**
 * Client Supabase configuré avec les best practices
 * Configuration centralisée pour une meilleure maintenabilité
 */

// Configuration pour le développement local
const supabaseUrl = import.meta.env.VITE_SUPABASE_URL || 'http://localhost:54321';
const supabaseAnonKey =
  import.meta.env.VITE_SUPABASE_ANON_KEY ||
  'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZS1kZW1vIiwicm9sZSI6ImFub24iLCJleHAiOjE5ODM4MTI5OTZ9.CRXP1A7WOeoJeXxjNni43kdQwgnWNReilDMblYTn_I0';

export const supabase = createClient(supabaseUrl, supabaseAnonKey, {
  auth: AUTH_CONSTANTS.SUPABASE_CONFIG.AUTH,
  global: AUTH_CONSTANTS.SUPABASE_CONFIG.GLOBAL,
});

// Types pour TypeScript basés sur ton schéma réel
export type Database = {
  public: {
    Tables: {
      orgs: {
        Row: {
          id: string;
          name: string;
          created_at: string;
        };
        Insert: {
          id?: string;
          name: string;
          created_at?: string;
        };
        Update: {
          id?: string;
          name?: string;
          created_at?: string;
        };
      };
      memberships: {
        Row: {
          user_id: string;
          org_id: string;
          role: 'admin' | 'member';
        };
        Insert: {
          user_id: string;
          org_id: string;
          role: 'admin' | 'member';
        };
        Update: {
          user_id?: string;
          org_id?: string;
          role?: 'admin' | 'member';
        };
      };
      contacts: {
        Row: {
          id: string;
          org_id: string;
          email: string;
          phone: string | null;
          first_name: string | null;
          last_name: string | null;
          school: string | null;
          status: 'PROSPECT' | 'PRE_INSCRIT' | 'INSCRIT' | 'REPORTE' | 'ANNULE';
          created_at: string;
          updated_at: string;
        };
        Insert: {
          id?: string;
          org_id: string;
          email: string;
          phone?: string | null;
          first_name?: string | null;
          last_name?: string | null;
          school?: string | null;
          status?: 'PROSPECT' | 'PRE_INSCRIT' | 'INSCRIT' | 'REPORTE' | 'ANNULE';
          created_at?: string;
          updated_at?: string;
        };
        Update: {
          id?: string;
          org_id?: string;
          email?: string;
          phone?: string | null;
          first_name?: string | null;
          last_name?: string | null;
          school?: string | null;
          status?: 'PROSPECT' | 'PRE_INSCRIT' | 'INSCRIT' | 'REPORTE' | 'ANNULE';
          created_at?: string;
          updated_at?: string;
        };
      };
      equipages: {
        Row: {
          id: string;
          org_id: string;
          name: string | null;
          status: 'PROSPECT' | 'PRE_INSCRIT' | 'INSCRIT' | 'REPORTE' | 'ANNULE';
          event: string;
          created_at: string;
          updated_at: string;
        };
        Insert: {
          id?: string;
          org_id: string;
          name?: string | null;
          status?: 'PROSPECT' | 'PRE_INSCRIT' | 'INSCRIT' | 'REPORTE' | 'ANNULE';
          event?: string;
          created_at?: string;
          updated_at?: string;
        };
        Update: {
          id?: string;
          org_id?: string;
          name?: string | null;
          status?: 'PROSPECT' | 'PRE_INSCRIT' | 'INSCRIT' | 'REPORTE' | 'ANNULE';
          event?: string;
          created_at?: string;
          updated_at?: string;
        };
      };
      equipage_members: {
        Row: {
          equipage_id: string;
          contact_id: string;
        };
        Insert: {
          equipage_id: string;
          contact_id: string;
        };
        Update: {
          equipage_id?: string;
          contact_id?: string;
        };
      };
      interactions: {
        Row: {
          id: string;
          org_id: string;
          contact_id: string | null;
          equipage_id: string | null;
          kind: 'note' | 'status_change' | 'email';
          subject: string | null;
          body: string | null;
          happened_at: string;
        };
        Insert: {
          id?: string;
          org_id: string;
          contact_id?: string | null;
          equipage_id?: string | null;
          kind: 'note' | 'status_change' | 'email';
          subject?: string | null;
          body?: string | null;
          happened_at?: string;
        };
        Update: {
          id?: string;
          org_id?: string;
          contact_id?: string | null;
          equipage_id?: string | null;
          kind?: 'note' | 'status_change' | 'email';
          subject?: string | null;
          body?: string | null;
          happened_at?: string;
        };
      };
      tasks: {
        Row: {
          id: string;
          org_id: string;
          title: string;
          due_at: string | null;
          done: boolean;
          related_contact_id: string | null;
          related_equipage_id: string | null;
          created_at: string;
        };
        Insert: {
          id?: string;
          org_id: string;
          title: string;
          due_at?: string | null;
          done?: boolean;
          related_contact_id?: string | null;
          related_equipage_id?: string | null;
          created_at?: string;
        };
        Update: {
          id?: string;
          org_id?: string;
          title?: string;
          due_at?: string | null;
          done?: boolean;
          related_contact_id?: string | null;
          related_equipage_id?: string | null;
          created_at?: string;
        };
      };
    };
    Functions: {
      user_in_org: {
        Args: {
          target_org: string;
        };
        Returns: boolean;
      };
    };
  };
};
